// frontend/src/components/MainMenu/MainMenu.jsx

import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTracking } from "react-tracking";
import { trackPromise } from "react-promise-tracker";
import { useDialogBox } from "src/hooks/useDialogBox";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";

// Redux slices and actions
import {
	setLod,
	setShowConsole,
	setShowTesting,
	setShowTimeoutCounter,
	setShowTimeoutShortWindow,
} from "src/slices/configsSlice";

// Layout/UI
import { Box, Avatar, AvatarGroup, Button, Icon, Menu, Portal, Stack, MetreaTooltip } from "@metrea-ui/v2";
import { NavigationFilled } from "@fluentui/react-icons";
import styles from "./MainMenu.module.css";

// Utilities
import { mColor } from "src/utils/themeUtils";
import {
	Session,
	isAdmin,
	isDeveloper,
	isStrat,
	isUserLogin,
	handleLogin,
	handleLogout,
	loadUserProfile,
	applyRedirectTarget,
	isDevTeam,
} from "src/utils/sessionUtils";
import { getUserStatusLabel } from "src/components/User/userStatus";
import { debugLog, showConsole as showConsoleFromAppLib } from "src/utils/debugUtils";

const menuItemStyle = {
	textDecoration: "none",
	fontSize: "0.8rem",
};

// Keep the actual navigation items shareable so both the top-left menu and the
// profile menu can expose the same routes without nesting one menu inside another.
const PrimaryNavigationMenuItems = ({ menuItemStyle, baselineModulePath }) => (
	<>
		{isDeveloper() && (
			<>
				<Menu.Item id="home" as={Link} to="/app" style={menuItemStyle}>
					App Home
				</Menu.Item>

				<Menu.Item id="landing" as={Link} to="/" style={menuItemStyle}>
					Landing Page
				</Menu.Item>

				<Menu.Item id="about" as={Link} to="/about" style={menuItemStyle}>
					About
				</Menu.Item>
				<Menu.Separator />
			</>
		)}
		{isUserLogin() && (
			<>
				<Menu.Item id="baseline-view" as={Link} to={baselineModulePath} style={menuItemStyle}>
					<MetreaTooltip content="Baseline" positioning={{ placement: "right-end" }}>
						<span>Baselines</span>
					</MetreaTooltip>
				</Menu.Item>
				<Menu.Item id="Scenario-edit" as={Link} to="/scenario" style={menuItemStyle}>
					<MetreaTooltip content="Scenario" positioning={{ placement: "right-end" }}>
						<span>Scenarios</span>
					</MetreaTooltip>
				</Menu.Item>
			</>
		)}
		{isStrat() && (
			<Menu.Item id="auction-view" as={Link} to="/auction" style={menuItemStyle}>
				<MetreaTooltip content="Auction" positioning={{ placement: "right-end" }}>
					<span>Auctions</span>
				</MetreaTooltip>
			</Menu.Item>
		)}
		{(isDeveloper() || isAdmin()) && <Menu.Separator />}
		{(isDeveloper() || isAdmin()) && (
			<>
				<Menu.Item id="admin" as={Link} to="/admin" style={menuItemStyle}>
					Admin
				</Menu.Item>
				<Menu.Item
					id="admin-user"
					as={Link}
					to="/admin/user"
					style={{ ...menuItemStyle, paddingLeft: "1.5rem" }}
				>
					<MetreaTooltip content="User Management" positioning={{ placement: "right-end" }}>
						<span>- User</span>
					</MetreaTooltip>
				</Menu.Item>
				{isDevTeam() && (
					<Menu.Item
						id="admin-user-activity"
						as={Link}
						to="/admin/activity"
						style={{ ...menuItemStyle, paddingLeft: "1.5rem" }}
					>
						<MetreaTooltip content="User Activity" positioning={{ placement: "right-end" }}>
							<span>- User Activity</span>
						</MetreaTooltip>
					</Menu.Item>
				)}
				{isDeveloper() && (
					<Menu.Item
						id="admin-user-group"
						as={Link}
						to="/admin/usergroup"
						style={{ ...menuItemStyle, paddingLeft: "1.5rem" }}
					>
						<MetreaTooltip
							content="User Group Management"
							positioning={{ placement: "right-end" }}
						>
							<span>- User Group</span>
						</MetreaTooltip>
					</Menu.Item>
				)}
			</>
		)}
	</>
);

const MainMenu = (props) => {
	const { trackEvent } = useTracking();

	const mainAnchorRef = useRef(null);
	const getMainAnchorRect = () => mainAnchorRef.current?.getBoundingClientRect();

	// Chakra Menu is uncontrolled by default; keep a hook in case you want to log open/close
	const onOpenChange = (details) => {
		// details: { open: boolean }
		debugLog(2, "[MainMenu][FLOW] menuOpenChange", { open: details?.open });
	};

	const logoutHandler = (event) => {
		// Keep sign-out on the explicit logout path instead of a competing client-side route change.
		event?.preventDefault?.();
		const currentUser = Session()?.currUser;
		debugLog(3, "[MainMenu][ACTION] logoutHandler", {
			email: currentUser?.email,
			id: currentUser?.id,
		});

		trackEvent({
			page: " - ",
			event: "userLogout",
			props: {
				email: currentUser?.email || null,
				userId: currentUser?.id ?? null,
				statusId: currentUser?.status_id ?? null,
			},
		});
		handleLogout();
	};

	const baselineModulePath = "/baseline";

	return (
		<Menu.Root
			id="raven-menu"
			onOpenChange={onOpenChange}
			positioning={{
				getAnchorRect: getMainAnchorRect,
				placement: "bottom-start",
				gutter: 6,
				strategy: "fixed",
			}}
		>
			{/* anchor element Popper will use for placement */}
			<span ref={mainAnchorRef} style={{ display: "inline-flex" }}>
				<Menu.Trigger asChild>
					<Button id="main-menu-button" variant="ghost" aria-haspopup="menu">
						<MetreaTooltip content="Main Menu" positioning={{ placement: "bottom-end" }}>
							<Icon
								as={NavigationFilled}
								boxSize="1.5rem"
								style={{ color: mColor.MidnightBlue }}
							/>
						</MetreaTooltip>
					</Button>
				</Menu.Trigger>
			</span>
			<Portal>
				<Menu.Positioner>
					<Menu.Content
						// Keep the menu readable without forcing a full-width popover.
						style={{ width: "max-content", minWidth: 220, maxWidth: 480 }}
					>
						<AuthenticatedTemplate>
							<PrimaryNavigationMenuItems
								menuItemStyle={menuItemStyle}
								baselineModulePath={baselineModulePath}
							/>
							<Menu.Separator />

							<Menu.Item
								id="signout"
								as={Link}
								to="/"
								style={menuItemStyle}
								onClick={logoutHandler}
							>
								Sign Out
							</Menu.Item>
						</AuthenticatedTemplate>
						<UnauthenticatedTemplate>
							<Menu.Separator />

							<Menu.Item
								id="signin"
								as={Link}
								to="/"
								style={menuItemStyle}
								onClick={handleLogin}
							>
								Sign In
							</Menu.Item>
						</UnauthenticatedTemplate>
					</Menu.Content>
				</Menu.Positioner>
			</Portal>
		</Menu.Root>
	);
};

export default MainMenu;

const debugLevelTooltip = {
	1: [
		"Record major application events, navigation, and top-level user actions that affect the whole app.",
		" ",
		"Examples: ",
		"App initialization, route/page changes, logins/logouts.  Major user actions: “User submitted scenario”, “Started auction”, “User switched modules”.  System events: auto-save, import/export started/completed.",
	],
	2: [
		"Use this to confirm when your app moves into/out of key areas or starts big processes",
		" ",
		"Examples: ",
		"Entering or leaving a module, list, or edit page.  Start/end of asynchronous processes: data fetch, save, batch update.  Module-wide triggers or resets",
	],
	3: [
		"Use this to confirm the flow of data between components, major prop changes, and business rule triggers.",
		" ",
		"Examples: ",
		"React props or state values (after user action, after API load, etc.)  “Checkpoint” data after significant user interaction or system event.  Major “decision” variables (e.g., which component mode is active, current user, selected item).",
	],
	4: [
		"Dig into how something was calculated, showing raw arrays/objects, loop variables, and intermediate steps that usually aren’t needed except for troubleshooting data bugs or edge cases.  Values that help explain why Level 3 data has its value",
		" ",
		"Examples: ",
		"Arrays/maps/objects being built or filtered during a calculation. Temporary/intermediate variables within a function.  Before/after states for data transforms or merges.  Values that help explain why Level 3 data has its value.",
	],
	9: ["Show deep debugging information."],
	99: ["Show everything."],
};

export const ProfileMenu = (props) => {
	const { trackEvent } = useTracking();
	const dispatch = useDispatch();
	const { showDialog } = useDialogBox();
	const baselineModulePath = "/baseline";
	const [showNavigationItems, setShowNavigationItems] = useState(false);

	const lod = useSelector((s) => s.configs.lod);
	const profileAnchorRef = useRef(null);
	const getProfileAnchorRect = () => profileAnchorRef.current?.getBoundingClientRect();

	const isActing = Session().currUser.id !== Session().loginUser.id ? true : false;

	const { showConsole, showTesting, showTimeoutCounter, showTimeoutShortWindow } = useSelector(
		(s) => s.configs,
	);

	const onOpenChange = (d) => debugLog(2, "[ProfileMenu][FLOW] menuOpenChange", { open: d?.open });

	const logoutHandler = (event) => {
		// Keep sign-out on the explicit logout path instead of a competing client-side route change.
		event?.preventDefault?.();
		const currentUser = Session()?.currUser;
		debugLog(3, "[ProfileMenu][ACTION] logoutHandler", {
			email: currentUser?.email,
			id: currentUser?.id,
		});

		trackEvent({
			page: " - ",
			event: "userLogout",
			props: {
				email: currentUser?.email || null,
				userId: currentUser?.id ?? null,
				statusId: currentUser?.status_id ?? null,
			},
		});
		handleLogout();
	};

	async function actingAsHandler(event, email) {
		if (showConsoleFromAppLib()) {
			console.log("MainMenu - actingAsHandler()");
			console.log("Switch back to: " + email);
		}

		const result = await trackPromise(loadUserProfile(email, "currUser", "admin/user", "switchBack"));
		if (!result?.ok) {
			debugLog(2, "[ProfileMenu][FLOW] loadUserProfile failed", { email });
			showDialog({
				title: "Error",
				message: ["Failed to switch back user profile.", "Please try again."],
				type: "error",
			});
			return;
		}

		if (applyRedirectTarget(result.redirectTarget)) {
			return;
		}
		// navigate("/");
		// window.location.replace(process.env.REACT_APP_URI);
	}

	const consoleLogHandler = () => {
		if (showConsole) {
			dispatch(setShowConsole(false));
			dispatch(setLod(-1));
			console.clear();
		} else {
			dispatch(setShowConsole(true));
			dispatch(setLod(1));
		}
	};

	const lodHandler = (event, level) => {
		// clear console logs before the LOD chagnes
		// console.clear();
		if (showConsole) {
			console.log("\n\n\n\n\n\n\n\n\n\n\n\n");
			console.log("Debug log level changed: ", level);
		}
		dispatch(setLod(level));
	};

	const showTestingHandler = (nextChecked) => {
		dispatch(setShowTesting(nextChecked));
	};

	// Separate from showTesting: controls timeout counter visibility only.
	const showTimeoutCounterHandler = (nextChecked) => {
		dispatch(setShowTimeoutCounter(nextChecked));
	};

	// Separate from showTesting: controls short timeout windows only.
	const showTimeoutShortWindowHandler = (nextChecked) => {
		dispatch(setShowTimeoutShortWindow(nextChecked));
	};

	const logLevels = [
		{ label: "(lv 1) high-Level events & user flows", value: 1 },
		{ label: "(lv 2) page/module entry & major OPs", value: 2 },
		{ label: "(lv 3) componet logic & major state, user-driven event handlers", value: 3 },
		{ label: "(lv 4) internal calculations, data structure", value: 4 },
		{ label: "(lv 9) deep debugging/firehose/full state", value: 9 },
		{ label: "(lv 10+) debugging only - ylee", value: 10 },
		// { label: "(ALL) Everything!", value: 99 },
	];

	const renderDebugLevelLabel = (item) => {
		const tooltipContent = debugLevelTooltip[item.value];
		const label = <span>{item.label}</span>;

		if (!tooltipContent) return label;

		return (
			<MetreaTooltip content={tooltipContent} positioning={{ placement: "left" }}>
				{label}
			</MetreaTooltip>
		);
	};

	const navigationSectionButtonStyle = {
		...menuItemStyle,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		width: "100%",
		padding: "0.5rem 0",
		background: "transparent",
		border: 0,
		outline: "none",
		boxShadow: "none",
		appearance: "none",
		WebkitAppearance: "none",
		cursor: "pointer",
		textAlign: "left",
	};

	return (
		<Menu.Root
			id="profile-menu"
			onOpenChange={onOpenChange}
			positioning={{
				getAnchorRect: getProfileAnchorRect,
				placement: "bottom-end",
				gutter: 6,
				strategy: "fixed",
			}}
			// closeOnSelect={false}
		>
			{/* anchor element Popper will use for placement */}
			<span ref={profileAnchorRef} style={{ display: "inline-flex" }}>
				<Menu.Trigger asChild>
					<button type="button" className={styles["avatar-trigger"]}>
						{/* padding-free trigger to eliminate the tiny built-in Button padding */}
						<MetreaTooltip content="Profile" positioning={{ placement: "bottom-end" }}>
							<AvatarGroup>
								<Avatar.Root size="2xs" color={isActing ? mColor.Electromagnetic : "unset"}>
									<Avatar.Fallback name={props.user.full_name} />
									<Avatar.Image />
								</Avatar.Root>
							</AvatarGroup>
						</MetreaTooltip>
					</button>
				</Menu.Trigger>
			</span>
			<Portal>
				<Menu.Positioner>
					<Menu.Content style={{ width: "max-content", minWidth: 220, maxWidth: 480 }}>
						{/* <AuthenticatedTemplate> */}
						<Stack direction="column" gap={0} padding={2}>
							<Box
								style={{
									...(isActing ? { color: mColor.Electromagnetic } : {}),
									fontWeight: "600",
								}}
								color={isActing ? mColor.Electromagnetic : "unset"}
							>
								{props.user.displayName}
							</Box>
							<Box style={{ fontSize: "0.8rem", paddingTop: "0.2rem" }}>
								<Box>
									{props.user.email} <br />
									{props.user.jobTitleLabel} <br />
								</Box>
								<Box style={{ paddingTop: "0.5rem" }}>
									User Role(s):
									<br />
									{props.user.roleLabel}
								</Box>
								{isActing && (
									<Box data-testid="account-status" style={{ paddingTop: "0.5rem" }}>
										Account Status:
										<br />
										{getUserStatusLabel(props.user.status_id)}
									</Box>
								)}
							</Box>
						</Stack>
						<Menu.Separator />
						<Menu.Item id="myProfile" as={Link} to="/myprofile" style={menuItemStyle}>
							Update My Profile
						</Menu.Item>
						{isDeveloper() && (
							<>
								<Menu.Separator />
								<Menu.Item id="test" as={Link} to="/test" style={menuItemStyle}>
									Testing Page
								</Menu.Item>
								{isDevTeam() && (
									<>
										<Menu.CheckboxItem
											id="showTesting"
											value="show-testing" // must be a string
											checked={!!showTesting}
											onCheckedChange={(arg) => {
												// arg can be boolean or { checked, value }
												const next = typeof arg === "boolean" ? arg : arg?.checked;
												if (typeof next === "boolean") {
													showTestingHandler(next);
												}
											}}
											style={menuItemStyle}
										>
											Show testing features
											<Menu.ItemIndicator />
										</Menu.CheckboxItem>

										<Menu.CheckboxItem
											id="showTimeoutCounter"
											value="show-timeout-counter"
											checked={!!showTimeoutCounter}
											onCheckedChange={(arg) => {
												// Fluent Menu can emit boolean or { checked, value }.
												const next = typeof arg === "boolean" ? arg : arg?.checked;
												if (typeof next === "boolean") {
													showTimeoutCounterHandler(next);
												}
											}}
											style={menuItemStyle}
										>
											Show timeout counter logs
											<Menu.ItemIndicator />
										</Menu.CheckboxItem>

										<Menu.CheckboxItem
											id="showTimeoutShortWindow"
											value="show-timeout-short-window"
											checked={!!showTimeoutShortWindow}
											onCheckedChange={(arg) => {
												const next = typeof arg === "boolean" ? arg : arg?.checked;
												if (typeof next === "boolean") {
													showTimeoutShortWindowHandler(next);
												}
											}}
											style={menuItemStyle}
										>
											<MetreaTooltip
												content="Testing only: uses short timeout windows and can force early logout."
												positioning={{ placement: "left" }}
											>
												<span>Use short timeout window</span>
											</MetreaTooltip>
											<Menu.ItemIndicator />
										</Menu.CheckboxItem>
									</>
								)}
								<Menu.Item id="profile" as={Link} to="/profile" style={menuItemStyle}>
									AD Profile
								</Menu.Item>

								{isDevTeam() && (
									<>
										<Menu.Separator />
										<div style={{ paddingLeft: "0.75rem" }}>
											<button
												type="button"
												style={navigationSectionButtonStyle}
												onClick={() => setShowNavigationItems((current) => !current)}
											>
												<span>Main Menu Navigation</span>
												<span aria-hidden="true">
													{showNavigationItems ? "[-]" : "[+]"}
												</span>
											</button>
											{showNavigationItems && (
												<div style={{ paddingLeft: "0.75rem" }}>
													<PrimaryNavigationMenuItems
														menuItemStyle={menuItemStyle}
														baselineModulePath={baselineModulePath}
													/>
												</div>
											)}
										</div>
										<Menu.Separator />
										<Menu.Item
											id="showConsole"
											as={Link}
											style={menuItemStyle}
											onClick={consoleLogHandler}
										>
											{!showConsole ? "Enable" : "Disable"} console logs
										</Menu.Item>
									</>
								)}

								{isDevTeam() && showConsole && (
									<>
										<Menu.RadioItemGroup
											value={lod}
											onValueChange={(e) => lodHandler(e, e.value)}
										>
											{logLevels.map((item) => (
												<Menu.RadioItem
													fontSize="0.75rem"
													key={item.value}
													value={item.value}
												>
													{renderDebugLevelLabel(item)}
													<Menu.ItemIndicator />
												</Menu.RadioItem>
											))}
											{isDevTeam() && (
												<Menu.RadioItem fontSize="0.75rem" key={99} value={99}>
													<MetreaTooltip
														content={debugLevelTooltip[99]}
														positioning={{ placement: "left" }}
													>
														<span>(ALL) Everything!</span>
													</MetreaTooltip>
													<Menu.ItemIndicator />
												</Menu.RadioItem>
											)}
										</Menu.RadioItemGroup>
									</>
								)}
							</>
						)}
						{isActing && (
							<>
								<Menu.Separator />
								<MetreaTooltip
									content={`Switch back to ${Session().loginUser.email}`}
									positioning={{ placement: "bottom-end" }}
								>
									<Menu.Item
										id="switchback"
										as={Link}
										to="/home"
										style={{
											...menuItemStyle,
											...(isActing ? { color: mColor.Electromagnetic } : {}),
										}}
										onClick={(event) => actingAsHandler(event, Session().loginUser.email)}
									>
										{`Switch back to ${Session().loginUser.full_name}`}
									</Menu.Item>
								</MetreaTooltip>
							</>
						)}
						<Menu.Separator />
						<Menu.Item
							id="signout"
							as={Link}
							to="/"
							style={menuItemStyle}
							onClick={logoutHandler}
						>
							Sign Out
						</Menu.Item>
						{/* </AuthenticatedTemplate> */}
					</Menu.Content>
				</Menu.Positioner>
			</Portal>
		</Menu.Root>
	);
};
